// TEMP(tmp_hide_new_ai_summary): remove when IP-15683 ships.
// Verbatim copy of the master IssuesGridFilterBar (FilterPanel + TextInput), used
// when ia_ipa_subprocesses_enabled is off. IssuesGridFilterBar is the IP-15683 version.
import React, { useCallback, useMemo } from 'react';
import { translate } from '@utils/i18n';
import FilterPanel from '@components/FilterPanel';
import FilterTypes from '@constants/FilterTypes';
import { TextInput } from '@ui/molecules/inputs/form/TextInput/TextInput';
import { IssueTypeEnum, IssueStatusEnum } from '../../types';
import { humanizeInspectableType } from '../../utils/humanizeInspectableType';
import { EMPTY_FILTERS } from './types';
const NS = 'features.iim_review.issues_grid';
const CARD_NS = 'features.iim_review.issue_card';
function getIssueTypeLabel(type) {
    return translate(type, { namespace: `${NS}.issue_type_labels`, defaultValue: type });
}
function getStatusLabel(status) {
    return translate(status, { namespace: `${CARD_NS}.status`, defaultValue: status });
}
export default function LegacyIssuesGridFilterBar({ filters, onFiltersChange, filterOptions, searchValue, onSearchChange, onResetSelection }) {
    const issueTypeOptions = useMemo(() => ((filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.issueTypes) || Object.values(IssueTypeEnum)).map((type) => ({
        title: getIssueTypeLabel(type),
        value: type
    })), [filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.issueTypes]);
    const statusOptions = useMemo(() => Object.values(IssueStatusEnum).map((status) => ({
        title: getStatusLabel(status),
        value: status
    })), []);
    const deviceTypeOptions = useMemo(() => ((filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.inspectableTypes) || []).map((type) => ({
        title: humanizeInspectableType(type),
        value: type
    })), [filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.inspectableTypes]);
    const impactOptions = useMemo(() => [
        { title: translate('high', { namespace: `${NS}.impact` }), value: 'high' },
        { title: translate('low', { namespace: `${NS}.impact` }), value: 'low' }
    ], []);
    const handleFilterChange = useCallback((field) => (value) => {
        var _a;
        const newFilters = Object.assign({}, filters);
        switch (field) {
            case 'issueType':
                newFilters.issueTypes = (Array.isArray(value) ? value : [value]).filter(Boolean);
                break;
            case 'status':
                newFilters.statuses = (Array.isArray(value) ? value : [value]).filter(Boolean);
                break;
            case 'deviceType':
                newFilters.inspectableTypes = (Array.isArray(value) ? value : [value]).filter(Boolean);
                break;
            case 'impact':
                // GenericMultiSelect returns array; single=true means one item max
                newFilters.impact = ((_a = (Array.isArray(value) ? value[0] : value)) !== null && _a !== void 0 ? _a : null);
                break;
        }
        onFiltersChange(newFilters);
    }, [filters, onFiltersChange]);
    const noopToggle = useCallback(() => undefined, []);
    const filtersList = useMemo(() => [
        {
            type: FilterTypes.GenericMultiSelect,
            field: 'issueType',
            locale: 'issue_type',
            active: true,
            value: filters.issueTypes,
            optionsForSelect: issueTypeOptions,
            disableSorting: true,
            onChange: handleFilterChange('issueType'),
            onToggle: noopToggle
        },
        {
            type: FilterTypes.GenericMultiSelect,
            field: 'status',
            locale: 'issue_status',
            active: true,
            value: filters.statuses,
            optionsForSelect: statusOptions,
            disableSorting: true,
            onChange: handleFilterChange('status'),
            onToggle: noopToggle
        },
        {
            type: FilterTypes.GenericMultiSelect,
            field: 'deviceType',
            locale: 'device_type',
            active: true,
            value: filters.inspectableTypes,
            optionsForSelect: deviceTypeOptions,
            disableSorting: true,
            onChange: handleFilterChange('deviceType'),
            onToggle: noopToggle
        },
        {
            type: FilterTypes.GenericMultiSelect,
            field: 'impact',
            locale: 'impact',
            active: true,
            value: filters.impact ? [filters.impact] : [],
            optionsForSelect: impactOptions,
            disableSorting: true,
            single: true,
            onChange: handleFilterChange('impact'),
            onToggle: noopToggle
        }
    ], [filters, issueTypeOptions, statusOptions, deviceTypeOptions, impactOptions, handleFilterChange, noopToggle]);
    const handleResetFilters = useCallback(() => {
        onFiltersChange(EMPTY_FILTERS);
        onSearchChange('');
        onResetSelection === null || onResetSelection === void 0 ? void 0 : onResetSelection();
    }, [onFiltersChange, onSearchChange, onResetSelection]);
    return (React.createElement("div", { className: "issues-grid-filters" },
        React.createElement(FilterPanel, { filters: filtersList, onResetFilters: handleResetFilters, onResetCache: handleResetFilters, toggleDisabled: true },
            React.createElement("div", { className: "issues-grid-filters__search" },
                React.createElement(TextInput, { id: "issues-grid-search", placeholder: translate('search_placeholder', { namespace: NS }), value: searchValue, onChange: (e) => onSearchChange(e.target.value) })))));
}
